import { isEmpty, isEqual, pickBy } from 'lodash';

const defaultSchemas = [
  { label: __('Email'), name: 'email', fields: ['email'] },
  { label: __('First name'), name: 'firstname', fields: ['firstname'] },
  { label: __('Last name'), name: 'lastname', fields: ['lastname'] },
  {
    label: ({ postal_address_type: postalAddressType = 'personal' }) =>
      postalAddressType === 'personal'
        ? I18n.t('web.networks.events.receipt.billing_location.postal_address')
        : I18n.t('web.networks.events.receipt.billing_location.work_address'),
    name: 'address',
    envelope: ({ postal_address_type: postalAddressType = 'personal' }) =>
      `postal_${postalAddressType}_attributes`,
    render: (data) => Object.values(data).join(', '),
    fields: [
      'address_1',
      'address_2',
      'address_3',
      'city',
      'state',
      'postal_code',
      'country',
    ],
    getUserData: (
      user,
      { postal_address_type: postalAddressType = 'personal' } = {},
    ) => user?.postal_address?.[postalAddressType],
    getFormData: (formData = {}) => formData.billing_address_attributes,
    comparator: (oldAddress, newAddress) =>
      !isEmpty(newAddress) && !isEqual(newAddress, oldAddress),
  },
  {
    label: __('Personal Phone Number'),
    name: 'mobile_perso',
    fields: ['mobile_perso'],
  },
];

const schemaReducer =
  ({ getSchema, user, formState }) =>
  (
    changesets,
    {
      fields,
      envelope,
      label,
      getUserData = (data) => data,
      getFormData = (data) => data,
      comparator = (from, to) => !isEmpty(to) && !isEqual(from, to),
      ...args
    },
  ) => {
    const formData = getFormData(formState);
    const userData = getUserData(user, formData);

    if (isEmpty(formData)) {
      return changesets;
    }

    const from = getSchema(userData, fields);
    const to = getSchema(formData, fields);

    const isModified = comparator(from, to);

    return [
      ...changesets,
      {
        ...args,
        envelope:
          typeof envelope === 'function' ? envelope(formData) : envelope,
        label: typeof label === 'function' ? label(formData) : label,
        isModified,
        ...(isModified
          ? {
              payload: {
                from,
                to,
              },
            }
          : []),
      },
    ];
  };

export const changeSetReducer = (
  { user, formState },
  customAttributes = [],
) => {
  const schemas = [
    ...defaultSchemas,
    ...customAttributes
      .filter(({ user_editable: userEditable }) => userEditable)
      .map((attribute) => ({
        type: attribute.type,
        label: attribute.display_name,
        name: attribute.name,
        fields: [attribute.name],
        getUserData: () => ({ [attribute.name]: attribute.value }),
      })),
  ];

  const getChangeSet = () => {
    if (!user || !formState) {
      return [];
    }

    const getSchema = (data, schema) =>
      pickBy(data, (value, prop) => value && schema.includes(prop));

    return schemas
      .reduce(schemaReducer({ getSchema, user, formState }), [])
      .filter(({ isModified }) => isModified);
  };

  return {
    getChangeSet,
  };
};
